// Dashboard contract — the shapes the customer-oriented project dashboard
// renders, shared between apps/server (which projects GitLab issues + the
// quality rollup + resource-label-events into them) and apps/web (which
// renders the result).
//
// GitLab issues are only opened/closed. The five Loom stages the dashboard
// renders (`needs_input → planned → in_progress → review → shipped`) are
// derived per project from a label mapping the project owner configures
// in `.loom.yml` (see `@loom/shared/config` `dashboard:` block). This file
// only defines the shape after derivation; the projection lives in
// apps/server.

import { z } from "zod";

// ---------------------------------------------------------------------------
// Stages
// ---------------------------------------------------------------------------

export const DASHBOARD_STAGES = [
  "needs_input",
  "planned",
  "in_progress",
  "review",
  "shipped",
] as const;

export type DashboardStage = (typeof DASHBOARD_STAGES)[number];

export const DashboardStageSchema = z.enum(DASHBOARD_STAGES);

// ---------------------------------------------------------------------------
// Issue (projected for the dashboard — not the raw GitLab object)
// ---------------------------------------------------------------------------

export const PRIORITIES = ["high", "normal", "low"] as const;
export type Priority = (typeof PRIORITIES)[number];

export const GATE_VERDICTS = ["pass", "fail", "n/a"] as const;
export type GateVerdict = (typeof GATE_VERDICTS)[number];

/**
 * Compact milestone summary carried on each `DashboardIssue` (and used as the
 * row shape of `GET /api/projects/:id/milestones`). Keeps the wire small —
 * the dashboard never needs the full GitLab milestone payload, only enough
 * to render a chip + the roadmap timeline.
 */
export const DashboardMilestoneSchema = z
  .object({
    id: z.number().int().nonnegative(),
    iid: z.number().int().nonnegative(),
    title: z.string(),
    description: z.string().nullable(),
    state: z.enum(["active", "closed"]),
    startDate: z.string().nullable(),
    dueDate: z.string().nullable(),
    webUrl: z.string().url().nullable(),
  })
  .strict();
export type DashboardMilestone = z.infer<typeof DashboardMilestoneSchema>;

export const DashboardIssueSchema = z
  .object({
    /** GitLab issue iid (per-project number, what users see as "#142"). */
    iid: z.number().int().nonnegative(),
    /** Display key, e.g. "#142". */
    key: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    /** Loom-derived stage from the per-project label mapping. */
    stage: DashboardStageSchema,
    priority: z.enum(PRIORITIES),
    /** DoR result. `n/a` when not yet evaluated; `fail` carries `dorReason`. */
    dor: z.enum(GATE_VERDICTS),
    dorReason: z.string().optional(),
    /** DoD result. `n/a` when the issue has not entered review/done. */
    dod: z.enum(GATE_VERDICTS),
    /** GitLab assignee handle, or null when unassigned. */
    assignee: z.string().nullable(),
    /** GitLab story-point weight, or null. */
    points: z.number().int().nullable(),
    /** All scoped labels currently on the issue (raw, no filtering). */
    labels: z.array(z.string()),
    /** Human age since creation, e.g. "3d". */
    age: z.string(),
    /** Human last-updated, e.g. "12m ago". */
    updated: z.string(),
    /** Raw ISO timestamp for sorting + freshness. */
    createdAt: z.string(),
    updatedAt: z.string(),
    /** ISO close timestamp; only set when stage === "shipped". */
    closedAt: z.string().nullable(),
    /** GitLab web URL. */
    webUrl: z.string().url(),
    /** Attached milestone, when the issue belongs to one. */
    milestone: DashboardMilestoneSchema.nullable(),
  })
  .strict();

export type DashboardIssue = z.infer<typeof DashboardIssueSchema>;

// ---------------------------------------------------------------------------
// KPI atoms (the design's `Trend` + sparkline shape)
// ---------------------------------------------------------------------------

export const KpiStatSchema = z
  .object({
    label: z.string(),
    note: z.string(),
    value: z.number(),
    unit: z.string(),
    /**
     * Period-over-period delta; positive means the value rose. The
     * dashboard combines this with `good` to colour the trend arrow.
     */
    delta: z.number().nullable(),
    /** Direction that counts as "good": `up` for %s, `down` for defects. */
    good: z.enum(["up", "down"]),
    /** Last-8-period sparkline values; oldest first. */
    spark: z.array(z.number()),
  })
  .strict();

export type KpiStat = z.infer<typeof KpiStatSchema>;

export const DashboardKpisSchema = z
  .object({
    // Q1 — captured well?
    dorFirstPass: KpiStatSchema,
    linkedContext: KpiStatSchema,
    legacyDebt: KpiStatSchema,
    // Q3 — actually good?
    quality: KpiStatSchema,
    dodFail: KpiStatSchema,
    defectRate: KpiStatSchema,
  })
  .strict();

export type DashboardKpis = z.infer<typeof DashboardKpisSchema>;

// ---------------------------------------------------------------------------
// Lead-time (Q2)
// ---------------------------------------------------------------------------

export const LEAD_TIME_CLASSES = ["expedite", "standard", "fixed"] as const;
export type LeadTimeClass = (typeof LEAD_TIME_CLASSES)[number];

export const LeadTimePointSchema = z
  .object({
    /** Days ago this issue closed. */
    x: z.number().nonnegative(),
    /** Days the issue took, start to finish. */
    y: z.number().nonnegative(),
    cls: z.enum(LEAD_TIME_CLASSES),
    iid: z.number().int().nonnegative(),
    title: z.string(),
  })
  .strict();

export type LeadTimePoint = z.infer<typeof LeadTimePointSchema>;

export const LeadTimeReadySchema = z
  .object({
    status: z.literal("ready"),
    medianDays: z.number().nonnegative(),
    items: z.array(LeadTimePointSchema),
    /** ISO timestamp the slow-block was computed at. */
    computedAt: z.string(),
  })
  .strict();

export const LeadTimePendingSchema = z
  .object({
    status: z.literal("pending"),
    computedAt: z.null(),
  })
  .strict();

export const LeadTimeSchema = z.discriminatedUnion("status", [
  LeadTimePendingSchema,
  LeadTimeReadySchema,
]);

export type LeadTime = z.infer<typeof LeadTimeSchema>;
export type LeadTimeReady = z.infer<typeof LeadTimeReadySchema>;
export type LeadTimePending = z.infer<typeof LeadTimePendingSchema>;

export const AgingWipSchema = z
  .object({
    oldestDays: z.number().nonnegative(),
    threshold: z.number().nonnegative(),
    /** GitLab issue key, e.g. "#127". */
    key: z.string(),
    title: z.string(),
  })
  .strict()
  .nullable();

export type AgingWip = z.infer<typeof AgingWipSchema>;

// ---------------------------------------------------------------------------
// Decisions inbox (Q4 — what needs the customer's call?)
// ---------------------------------------------------------------------------

export const DECISION_KINDS = ["clarify", "proposal", "blocked"] as const;
export type DecisionKind = (typeof DECISION_KINDS)[number];

/**
 * Inbox sources, in display order of priority:
 *   - `dor_fail`   — issue with a DoR-fail verdict and missing fields.
 *   - `decision_label` — issue carries a project-configured `decision_labels` mark.
 *   - `proposal`   — issue created by intake awaiting a customer review.
 */
export const DECISION_SOURCES = ["dor_fail", "decision_label", "proposal"] as const;
export type DecisionSource = (typeof DECISION_SOURCES)[number];

export const DecisionSchema = z
  .object({
    id: z.string(),
    source: z.enum(DECISION_SOURCES),
    kind: z.enum(DECISION_KINDS),
    action: z.string(),
    /** Optional count (e.g. "Review · 2" when several stories are batched). */
    count: z.number().int().positive().optional(),
    title: z.string(),
    /** Display ref ("#127" or freeform tag for proposals). */
    ref: z.string(),
    body: z.string(),
    /** Missing-field list for `dor_fail`. */
    missing: z.array(z.string()).optional(),
    /** Handle of the user the decision is waiting on; null for unassigned. */
    who: z.string().nullable(),
    age: z.string(),
    /** GitLab issue iid this decision points at, if any. */
    iid: z.number().int().nonnegative().optional(),
  })
  .strict();

export type Decision = z.infer<typeof DecisionSchema>;

// ---------------------------------------------------------------------------
// Top-level aggregate
// ---------------------------------------------------------------------------

export const PROJECT_HEALTH = ["green", "amber", "red"] as const;
export type ProjectHealth = (typeof PROJECT_HEALTH)[number];

export const DashboardSummarySchema = z
  .object({
    openCount: z.number().int().nonnegative(),
    decisionCount: z.number().int().nonnegative(),
    /** Short eyebrow line above the H1, e.g. "Per-line VAT release · your project". */
    eyebrow: z.string().nullable(),
    /** Derived from decisions + aging-WIP: green / amber / red. */
    health: z.enum(PROJECT_HEALTH),
  })
  .strict();

export type DashboardSummary = z.infer<typeof DashboardSummarySchema>;

export const DashboardDataSchema = z
  .object({
    projectId: z.number().int().nonnegative(),
    summary: DashboardSummarySchema,
    kpis: DashboardKpisSchema,
    leadTime: LeadTimeSchema,
    agingWip: AgingWipSchema,
    decisions: z.array(DecisionSchema),
  })
  .strict();

export type DashboardData = z.infer<typeof DashboardDataSchema>;

// ---------------------------------------------------------------------------
// Slow block — lead-time + event-derived aging-WIP, fetched separately
// ---------------------------------------------------------------------------

/**
 * Body of `GET /api/projects/:id/dashboard/lead-time`. The fast `/dashboard`
 * call always lands first with `leadTime.status === "pending"`; this
 * follow-up resolves the lead-time scatter and a refined event-derived
 * aging-WIP. The UI swaps both into place on `status === "ready"`.
 */
export const DashboardLeadTimeResponseSchema = z.discriminatedUnion("status", [
  z
    .object({
      status: z.literal("pending"),
    })
    .strict(),
  z
    .object({
      status: z.literal("ready"),
      leadTime: LeadTimeReadySchema,
      agingWip: AgingWipSchema,
    })
    .strict(),
]);

export type DashboardLeadTimeResponse = z.infer<
  typeof DashboardLeadTimeResponseSchema
>;

// ---------------------------------------------------------------------------
// Issue editor — request/response wire for create + edit
// ---------------------------------------------------------------------------

/** Free-form acceptance-criterion line — what the IssueEditor binds to. */
export const AcceptanceCriterionSchema = z.string().min(1);

export const IssueEditorPayloadSchema = z
  .object({
    title: z.string().trim(),
    description: z.string(),
    /** Business outcome — surfaced separately by the editor; persisted in body. */
    outcome: z.string(),
    acceptanceCriteria: z.array(AcceptanceCriterionSchema),
    priority: z.enum(PRIORITIES),
  })
  .strict();

export type IssueEditorPayload = z.infer<typeof IssueEditorPayloadSchema>;

/** Body of POST /api/projects/:id/issues/:iid/stage. */
export const StageMovePayloadSchema = z
  .object({
    /** Target stage; backend swaps the configured labels accordingly. */
    target: DashboardStageSchema,
  })
  .strict();

export type StageMovePayload = z.infer<typeof StageMovePayloadSchema>;

// ---------------------------------------------------------------------------
// Phase 2d — methodology intake on POST /issues
// ---------------------------------------------------------------------------

/**
 * A link the user accepted on the proposals panel and that the apply seam
 * should create alongside the issue. Edge type defaults to the candidate's
 * own `edgeType` from `propose_links`; the editor stores it verbatim.
 */
export const AcceptedLinkSchema = z
  .object({
    targetIid: z.number().int().nonnegative(),
    edgeType: z.enum(["relates_to", "blocks", "blocked_by", "parent"]),
  })
  .strict();
export type AcceptedLink = z.infer<typeof AcceptedLinkSchema>;

// ---------------------------------------------------------------------------
// Complexity scoring — emitted by the intake apply seam so the UI can offer
// a "make this a milestone?" affordance for big stories.
// ---------------------------------------------------------------------------

/**
 * Heuristic-derived complexity readout the apply seam emits after creating an
 * issue, surfaced in `IntakeResponse.complexity`. Above the configured
 * threshold the UI shows a "make this a milestone?" affordance.
 */
export const ComplexityScoreSchema = z
  .object({
    /** Integer 0..100. */
    score: z.number().int().min(0).max(100),
    /** True when score >= threshold and the dashboard should nudge the user. */
    suggestsMilestone: z.boolean(),
    /** Short human rationale shown in the suggestion banner. */
    rationale: z.string(),
    /** The configured threshold the score was compared against. */
    threshold: z.number().int().min(0).max(100),
  })
  .strict();
export type ComplexityScore = z.infer<typeof ComplexityScoreSchema>;

/** Body of POST /api/projects/:id/issues (Phase 2d). */
export const IntakeApplyRequestSchema = z
  .object({
    payload: IssueEditorPayloadSchema,
    accepted: z.array(AcceptedLinkSchema).default([]),
  })
  .strict();
export type IntakeApplyRequest = z.infer<typeof IntakeApplyRequestSchema>;

/**
 * Response from the intake apply seam (the JSON shape; the SSE channel
 * emits this under a final `event: done` frame).
 */
export const IntakeResponseSchema = z
  .object({
    issue: DashboardIssueSchema,
    dor: z
      .object({
        verdict: z.enum(["pass", "fail"]),
        missing: z.array(z.string()),
      })
      .strict()
      .nullable(),
    /** True when `ANTHROPIC_API_KEY` is absent and DoR/proposals were skipped. */
    methodologySkipped: z.boolean(),
    /**
     * Optional complexity scoring of the just-created issue. Absent when
     * scoring is skipped (e.g. trivial body, scorer disabled). When
     * `suggestsMilestone` is true the dashboard surfaces a "make this a
     * milestone?" affordance.
     */
    complexity: ComplexityScoreSchema.optional(),
  })
  .strict();
export type IntakeResponse = z.infer<typeof IntakeResponseSchema>;

/** Per-phase progress frame for the SSE stream. */
export const INTAKE_PROGRESS_PHASES = [
  "propose_links",
  "dor_check",
  "creating",
] as const;
export type IntakeProgressPhase = (typeof INTAKE_PROGRESS_PHASES)[number];

export const IntakeProgressEventSchema = z
  .object({
    phase: z.enum(INTAKE_PROGRESS_PHASES),
    step: z.string(),
    message: z.string().optional(),
  })
  .strict();
export type IntakeProgressEvent = z.infer<typeof IntakeProgressEventSchema>;

// ---------------------------------------------------------------------------
// Phase 2d — propose-links route (called from the editor while drafting)
// ---------------------------------------------------------------------------

/** Body of POST /api/projects/:id/issues/draft/propose-links. */
export const ProposeLinksRequestSchema = z
  .object({
    payload: IssueEditorPayloadSchema,
  })
  .strict();
export type ProposeLinksRequest = z.infer<typeof ProposeLinksRequestSchema>;

export const LinkCandidateRowSchema = z
  .object({
    iid: z.number().int().nonnegative(),
    title: z.string(),
    edgeType: z.enum(["relates_to", "blocks", "blocked_by", "parent"]),
    confidence: z.number().min(0).max(1),
    rationale: z.string(),
  })
  .strict();
export type LinkCandidateRow = z.infer<typeof LinkCandidateRowSchema>;

export const ProposeLinksResponseSchema = z
  .object({
    candidates: z.array(LinkCandidateRowSchema),
    /** Skipped when no ANTHROPIC_API_KEY — UI shows the same pill the apply path does. */
    methodologySkipped: z.boolean(),
  })
  .strict();
export type ProposeLinksResponse = z.infer<typeof ProposeLinksResponseSchema>;

// ---------------------------------------------------------------------------
// Phase 2d — issue timeline (stage transitions from resource_label_events)
// ---------------------------------------------------------------------------

export const ISSUE_TIMELINE_KINDS = [
  "stage_in",
  "stage_out",
  "label_add",
  "label_remove",
] as const;
export type IssueTimelineKind = (typeof ISSUE_TIMELINE_KINDS)[number];

export const IssueTimelineEventSchema = z
  .object({
    at: z.string(),
    kind: z.enum(ISSUE_TIMELINE_KINDS),
    stage: DashboardStageSchema.optional(),
    label: z.string(),
    actor: z.string().nullable(),
  })
  .strict();
export type IssueTimelineEvent = z.infer<typeof IssueTimelineEventSchema>;

export const IssueTimelineResponseSchema = z
  .object({
    events: z.array(IssueTimelineEventSchema),
  })
  .strict();
export type IssueTimelineResponse = z.infer<typeof IssueTimelineResponseSchema>;

// ---------------------------------------------------------------------------
// Phase 3a — Conversations + Project activity accordions
// ---------------------------------------------------------------------------

/**
 * Mode enum the design's thread-card stripe colour and the activity-feed
 * mode-tag both read from. The sessions index only carries `pm | coding`;
 * `intake` and `meeting` are virtual modes the design surfaces but the
 * orchestrator doesn't yet model — the projection clamps anything
 * unfamiliar back onto `pm` so the UI always gets a known stripe.
 */
export const THREAD_MODES = ["pm", "coding", "intake", "meeting"] as const;
export type ThreadMode = (typeof THREAD_MODES)[number];

export const ProjectThreadSchema = z
  .object({
    sessionId: z.string(),
    title: z.string(),
    /** Short preview line — first non-trivial user message if known. */
    preview: z.string(),
    mode: z.enum(THREAD_MODES),
    /** Live chat WS attached right now. Drives the green-dot affordance. */
    live: z.boolean(),
    /** Human last-active, e.g. "12m ago". */
    updated: z.string(),
    /** Raw ISO timestamp for client-side sort/freshness. */
    lastActiveAt: z.string(),
  })
  .strict();

export type ProjectThread = z.infer<typeof ProjectThreadSchema>;

export const ProjectThreadsResponseSchema = z
  .object({
    threads: z.array(ProjectThreadSchema),
  })
  .strict();
export type ProjectThreadsResponse = z.infer<
  typeof ProjectThreadsResponseSchema
>;

/**
 * Activity-feed event kinds — drives the timeline icon + dot colour the
 * design renders via `.memory-item.<kind>`. Stage transitions and
 * session/intake signals collapse onto this enum at projection time.
 */
export const ACTIVITY_KINDS = [
  "issue",
  "intake",
  "bridge",
  "groom",
  "link",
  "gate",
  "coach",
] as const;
export type ActivityKind = (typeof ACTIVITY_KINDS)[number];

export const ActivityEventSchema = z
  .object({
    /** ISO timestamp the event happened at. */
    at: z.string(),
    kind: z.enum(ACTIVITY_KINDS),
    text: z.string(),
    /** Display actor — GitLab username, "Loom", "Coach", etc. */
    actor: z.string(),
    /** Optional issue/MR/session ref — UI uses for deep-links when present. */
    ref: z.string().optional(),
  })
  .strict();
export type ActivityEvent = z.infer<typeof ActivityEventSchema>;

export const ProjectActivityResponseSchema = z
  .object({
    events: z.array(ActivityEventSchema),
  })
  .strict();
export type ProjectActivityResponse = z.infer<
  typeof ProjectActivityResponseSchema
>;

// ---------------------------------------------------------------------------
// Phase 3b — setup write, bulk reweight, decompose, SSE invalidation
// ---------------------------------------------------------------------------

const StageLabelMappingSchema = z
  .object({
    needs_input: z.array(z.string()),
    planned: z.array(z.string()),
    in_progress: z.array(z.string()),
    review: z.array(z.string()),
    shipped: z.array(z.string()),
  })
  .strict();

/** Body of PUT /api/projects/:id/dashboard/config. */
export const ApplyMappingPayloadSchema = z
  .object({
    mapping: StageLabelMappingSchema,
    decisionLabels: z.array(z.string()),
  })
  .strict();
export type ApplyMappingPayload = z.infer<typeof ApplyMappingPayloadSchema>;

export const ApplyMappingResponseSchema = z
  .object({
    configured: z.literal(true),
    mapping: StageLabelMappingSchema,
    decisionLabels: z.array(z.string()),
    commitSha: z.string(),
  })
  .strict();
export type ApplyMappingResponse = z.infer<typeof ApplyMappingResponseSchema>;

/**
 * Decompose response — a flattened list of proposed children the UI renders
 * in the IssueEditor's `ProposalsPanel` (children-mode discriminator). The
 * full workflow tree is collapsed at the route boundary; the apply step
 * later turns each entry into a `create_issue` call.
 */
export const DecomposeChildSchema = z
  .object({
    parentIid: z.number().int().nonnegative(),
    title: z.string().min(1),
    description: z.string(),
    /** INVEST summary from the workflow output. */
    investSummary: z
      .object({
        small: z.enum(["ok", "warn", "fail"]),
        independent: z.enum(["ok", "warn", "fail"]),
        testable: z.enum(["ok", "warn", "fail"]),
      })
      .strict(),
  })
  .strict();
export type DecomposeChild = z.infer<typeof DecomposeChildSchema>;

export const DecomposeResponseSchema = z
  .object({
    children: z.array(DecomposeChildSchema),
    /** True when no `ANTHROPIC_API_KEY` is wired — UI renders the same pill. */
    methodologySkipped: z.boolean(),
  })
  .strict();
export type DecomposeResponse = z.infer<typeof DecomposeResponseSchema>;

/** Per-phase progress frame for the decompose SSE stream. */
export const DECOMPOSE_PHASES = ["decomposing", "scoring", "done"] as const;
export type DecomposePhase = (typeof DECOMPOSE_PHASES)[number];

export const DecomposeProgressEventSchema = z
  .object({
    phase: z.enum(DECOMPOSE_PHASES),
    step: z.string(),
    message: z.string().optional(),
  })
  .strict();
export type DecomposeProgressEvent = z.infer<
  typeof DecomposeProgressEventSchema
>;

/**
 * Reasons a dashboard-invalidation event fires. The SPA receives the event
 * verbatim; it doesn't switch on reason today (every reason triggers the
 * same debounced refetch fan-out), but the field preserves provenance for
 * future per-reason invalidation routing.
 */
export const DASHBOARD_EVENT_REASONS = [
  "issue_created",
  "issue_updated",
  "issue_decomposed",
  "stage_moved",
  "quality_recorded",
  "mr_opened",
  "config_written",
  "milestone_created",
  "milestone_updated",
  "issue_milestone_changed",
] as const;
export type DashboardEventReason = (typeof DASHBOARD_EVENT_REASONS)[number];

export const DashboardEventSchema = z
  .object({
    reason: z.enum(DASHBOARD_EVENT_REASONS),
    /**
     * The affected issue's iid, when the write site knows it. Lets a
     * per-issue subscriber (the issue page) skip refetches for *other*
     * issues. Absent on the bridge stream-tap fallback (which only sees
     * tool names, not the iid) and on project-scoped writes (config,
     * milestone-created) — subscribers treat an absent iid as "refetch".
     */
    iid: z.number().int().positive().optional(),
  })
  .strict();
export type DashboardEvent = z.infer<typeof DashboardEventSchema>;

// ---------------------------------------------------------------------------
// Milestones + roadmap
// ---------------------------------------------------------------------------

/**
 * Per-milestone roll-up used by the dashboard roadmap. The backend computes
 * issue counts + closed counts so the UI can render a progress bar without a
 * second round-trip per milestone.
 *
 * The three count keys are OPTIONAL because `?counts=none` omits them: they
 * cost a full offset-paginated enumeration of every issue in the project, and
 * the milestone PICKERS read none of them. `.optional()` and deliberately not
 * `.nullable()` — an old client never sends the query param and still gets
 * counts, while `null` inside `.strict()` would fail the whole response parse
 * and drop the picker into its degraded free-text branch during a deploy skew.
 */
export const MilestoneRoadmapRowSchema = z
  .object({
    milestone: DashboardMilestoneSchema,
    issueCount: z.number().int().nonnegative().optional(),
    closedCount: z.number().int().nonnegative().optional(),
    /** Aggregated story-point weight of all attached issues (null = unweighted,
     *  absent = not computed for this request). */
    weight: z.number().int().nonnegative().nullable().optional(),
  })
  .strict();
export type MilestoneRoadmapRow = z.infer<typeof MilestoneRoadmapRowSchema>;

export const MilestoneRoadmapResponseSchema = z
  .object({
    milestones: z.array(MilestoneRoadmapRowSchema),
  })
  .strict();
export type MilestoneRoadmapResponse = z.infer<typeof MilestoneRoadmapResponseSchema>;

/** Body of POST /api/projects/:id/milestones. */
export const CreateMilestoneRequestSchema = z
  .object({
    title: z.string().trim().min(1),
    description: z.string().optional(),
    startDate: z.string().optional(),
    dueDate: z.string().optional(),
  })
  .strict();
export type CreateMilestoneRequest = z.infer<typeof CreateMilestoneRequestSchema>;

/** Body of PUT /api/projects/:id/issues/:iid/milestone. */
export const AssignIssueMilestonePayloadSchema = z
  .object({
    /** GitLab milestone id (the absolute id, not iid). null detaches. */
    milestoneId: z.number().int().nonnegative().nullable(),
  })
  .strict();
export type AssignIssueMilestonePayload = z.infer<typeof AssignIssueMilestonePayloadSchema>;

// ---------------------------------------------------------------------------
// Per-issue detail page — links + related MRs + GitLab-style activity
// ---------------------------------------------------------------------------

export const ISSUE_LINK_SOURCES = ["gitlab_link", "ref"] as const;
export type IssueLinkSource = (typeof ISSUE_LINK_SOURCES)[number];

export const ISSUE_LINK_EDGE_TYPES = [
  "relates_to",
  "blocks",
  "is_blocked_by",
] as const;
export type IssueLinkEdgeType = (typeof ISSUE_LINK_EDGE_TYPES)[number];

/**
 * One row in the detail-page links panel. The `source` tag tells the UI
 * whether GitLab itself records the link (`gitlab_link` — survives renames
 * and shows up in `/links`) or whether it's only a `#nnn` body reference
 * we extracted ourselves (`ref` — informal, may be stale). `gitlab_link`
 * rows additionally carry an `edgeType` (`relates_to | blocks | is_blocked_by`).
 */
export const IssueLinkRowSchema = z
  .object({
    iid: z.number().int().nonnegative(),
    title: z.string(),
    stage: DashboardStageSchema,
    state: z.enum(["opened", "closed"]),
    webUrl: z.string().url(),
    source: z.enum(ISSUE_LINK_SOURCES),
    edgeType: z.enum(ISSUE_LINK_EDGE_TYPES).optional(),
  })
  .strict();
export type IssueLinkRow = z.infer<typeof IssueLinkRowSchema>;

export const MR_STATES = ["opened", "closed", "merged", "locked"] as const;
export type MrState = (typeof MR_STATES)[number];

export const LinkedMrSchema = z
  .object({
    iid: z.number().int().nonnegative(),
    title: z.string(),
    state: z.enum(MR_STATES),
    webUrl: z.string().url(),
    sourceBranch: z.string(),
    targetBranch: z.string(),
    mergedAt: z.string().nullable(),
    author: z.string().nullable(),
    /**
     * Does this MR's description say it CLOSES the focal issue
     * (`mrClosesIssue`, apps/server/src/dashboard/issue-detail.ts)?
     *
     * The list itself is GitLab's `related_merge_requests` — "MRs that MENTION
     * this issue" — and Loom's own split→implement seed makes that over-match
     * structural: the implement prompt has the agent read the `_Part of #N_`
     * parent and write `Closes #<iid>`, so the one MR that closes a sub-issue
     * also name-drops its epic parent and every sibling. The implement
     * surface's duplicate-build guard keys on this, never on `state` alone.
     *
     * OPTIONAL because THIS SCHEMA IS `.strict()`: during a rolling deploy the
     * bundle and the server disagree about which keys exist, and a strict parse
     * turns that into a thrown `IssueDetailResponseSchema.parse` that takes the
     * whole issue-detail page down — not just the guard. Optional here, absent
     * from an old server, is the only version of "additive" that actually is.
     * A consumer reading `undefined` must degrade the same way it degrades on
     * a failed fetch.
     */
    closesIssue: z.boolean().optional(),
  })
  .strict();
export type LinkedMr = z.infer<typeof LinkedMrSchema>;

export const IssueDetailResponseSchema = z
  .object({
    issue: DashboardIssueSchema,
    links: z.array(IssueLinkRowSchema),
    mergeRequests: z.array(LinkedMrSchema),
    /**
     * Present when the focal issue is a split epic (`loom::epic` label):
     * the child iids parsed SERVER-SIDE from its `## Sub-issues` task-list
     * (parsing stays off the client — no regex there). Drives the links
     * panel's "Sub-issues" group + progress line.
     */
    epicChildIids: z.array(z.number().int().positive()).optional(),
    /**
     * Present when the focal issue is a breakdown child: its epic parent,
     * derived from the `_Part of #N_` backlink the apply writes (title
     * resolved from the already-fetched link rows). Drives the breadcrumb.
     */
    epicParent: z
      .object({
        iid: z.number().int().positive(),
        title: z.string(),
      })
      .strict()
      .optional(),
  })
  .strict();
export type IssueDetailResponse = z.infer<typeof IssueDetailResponseSchema>;

/**
 * Kinds the GitLab-style per-issue activity feed surfaces. `note` is a
 * user comment, `system_note` is a GitLab system-generated note we
 * couldn't trace back to a resource event (e.g. "changed description"),
 * the rest mirror the resource-events endpoints we read.
 */
export const ISSUE_ACTIVITY_KINDS = [
  "note",
  "system_note",
  "state_change",
  "label",
  "milestone",
] as const;
export type IssueActivityKind = (typeof ISSUE_ACTIVITY_KINDS)[number];

export const IssueActivityEventSchema = z
  .object({
    at: z.string(),
    kind: z.enum(ISSUE_ACTIVITY_KINDS),
    actor: z.string().nullable(),
    text: z.string(),
    /** Optional secondary label — e.g. the milestone title, the label name. */
    detail: z.string().optional(),
  })
  .strict();
export type IssueActivityEvent = z.infer<typeof IssueActivityEventSchema>;

export const IssueActivityResponseSchema = z
  .object({
    events: z.array(IssueActivityEventSchema),
  })
  .strict();
export type IssueActivityResponse = z.infer<typeof IssueActivityResponseSchema>;

